// Pure display helpers for the /finance/income page: labels, event
// descriptions and the summary / payslip row models the page renders.
// No DOM — unit-tested in test/model/finance/income-view.test.js.

import { formatMoney, formatMoneyValue, formatPercent, toAudCents } from './money.js';
import { currentFy, daysBetween, fyOf, fyLabel, toYmd } from './fy.js';
import { grantTranches, vestDates } from './pay-timeline.js';
import { bonusTargetActive, packageOn, resolveReviews } from './income.js';
import { audRateFor } from './market.js';

export const FREQUENCY_LABELS = Object.freeze({
  weekly: 'Weekly',
  fortnightly: 'Fortnightly',
  'half-monthly': 'Twice monthly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
});

export const EVENT_KIND_LABELS = Object.freeze({
  review: 'Pay review',
  'one-off': 'One-off payment',
  leave: 'Unpaid leave',
});

export const EVENT_KIND_ICONS = Object.freeze({
  review: 'fa-arrow-trend-up',
  'one-off': 'fa-coins',
  leave: 'fa-calendar-minus',
});

/** Month names for the bonus payout-day picker (index 0 = January). */
export const MONTH_NAMES = Object.freeze(
  Array.from({ length: 12 }, (_, i) =>
    new Date(2000, i, 1).toLocaleDateString('en-AU', { month: 'long' }),
  ),
);

const wholeMoney = (cents) => formatMoney(cents, { whole: true });

/** "15 Aug 2025" from 'YYYY-MM-DD' (local components, never UTC). */
export function shortDate(iso) {
  const { y, m, d } = toYmd(iso);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

/** "Monthly (15th)", "Twice monthly (15th + last day)", "Fortnightly". */
export function describeSchedule(pay) {
  const label = FREQUENCY_LABELS[pay.frequency] ?? pay.frequency;
  const day = (d) => (d >= 31 ? 'last day' : ordinal(d));
  if (pay.frequency === 'half-monthly') return `${label} (${pay.days.map(day).join(' + ')})`;
  if (pay.frequency === 'monthly' || pay.frequency === 'quarterly') {
    return `${label} (${day(toYmd(pay.anchor).d)})`;
  }
  return label;
}

/** "1 Jul 2025 – present" / "1 Jul 2020 – 30 Jun 2024". */
export function describeTenure(employment) {
  const end = employment.endDate ? shortDate(employment.endDate) : 'present';
  return `${shortDate(employment.startDate)} – ${end}`;
}

/**
 * The resolved package (see income.js `resolveReviews`) in effect on `date`,
 * else the earliest future one, or null with no reviews.
 */
export function currentPackage(events, date) {
  const resolved = resolveReviews(events);
  return packageOn(resolved, date) ?? resolved[0] ?? null;
}

/** "12%" / "Super Guarantee rate" (rate null = the SG rate). */
function superValue(rate) {
  return rate === null || rate === undefined ? 'Super Guarantee rate' : formatPercent(rate);
}

/** An amount part's value — a stored money value or derived AUD cents. */
function amountText(value, opts = {}) {
  return value && typeof value === 'object'
    ? formatMoneyValue(value, opts)
    : formatMoney(value, opts);
}

/** "5% of pay" / "AU$100.00 per pay" / "None" (null = not set). */
function sacrificeValue(s) {
  if (!s) return null;
  if (s.kind === 'none') return 'None';
  return s.kind === 'percent'
    ? `${formatPercent(s.value)} of pay`
    : `${amountText(s.value)} per pay`;
}

/**
 * "20% of salary" / "AU$10,000" / "None" (null = not set). A `resolved`
 * package's target (plain cents) is only kept while active.
 */
function bonusTargetValue(t, { resolved = false } = {}) {
  if (!t) return null;
  if (!resolved && !bonusTargetActive(t)) return 'None';
  return t.kind === 'percent'
    ? `${formatPercent(t.value)} of salary`
    : amountText(t.value, { whole: true });
}

/** "quarterly" / "monthly" / … / "every 2 months" for a vesting interval. */
function cadence(months) {
  const named = { 1: 'monthly', 3: 'quarterly', 6: 'half-yearly', 12: 'yearly' };
  return named[months] ?? `every ${months} months`;
}

/** "16 tranches quarterly from 18 Nov 2026" (+ any leave delay note). */
function vestingValue(tranches, intervalMonths, firstVest, delayNote) {
  return `${count(tranches, 'tranche', 'tranches')} ${cadence(intervalMonths)} from ${shortDate(firstVest)}${
    delayNote ? ` · ${delayNote}` : ''
  }`;
}

/** "30 September" from a bonus payout 'MM-DD'. */
export function describePayoutDay(payout) {
  const [mm, dd] = payout.split('-').map(Number);
  return `${dd} ${MONTH_NAMES[mm - 1]}`;
}

const count = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * An employment card's facts as detail-list items (`[{ key, label, value }]`,
 * null values skipped): tenure, pay schedule, the current package `pkg` (a
 * resolved package, or null before the first review), the bonus target + payout
 * day (only when the job `bonusOffered`), then the notes.
 */
export function employmentDetails(emp, pkg, { bonusOffered = false } = {}) {
  return [
    { key: 'tenure', label: 'Employed', value: describeTenure(emp) },
    { key: 'pay', label: 'Paid', value: describeSchedule(emp.pay) },
    { key: 'base', label: 'Salary', value: pkg ? wholeMoney(pkg.annualCents) : null },
    { key: 'super', label: 'Super', value: pkg ? superValue(pkg.superRate) : null },
    { key: 'sacrifice', label: 'Sacrifice', value: sacrificeValue(pkg?.sacrifice) },
    {
      key: 'bonus',
      label: 'Bonus',
      value:
        pkg?.bonusTarget && bonusOffered
          ? `${bonusTargetValue(pkg.bonusTarget, { resolved: true })} · paid ${describePayoutDay(emp.bonus.payout)}`
          : null,
    },
    { key: 'notes', label: 'Notes', value: emp.notes || null },
  ];
}

/**
 * Card text for a comp event: `{ title, details: [{ key, label, value }] }`
 * (detail-list items; null values skipped) — a review lists only the parts it
 * CHANGES (the rest carry forward); `fx` = AUD per USD (latest known; null = no
 * rate, so the AUD estimate is omitted); `leaves` (the employment's
 * `leavePeriods`) shows any vesting delay on a grant.
 */
export function describeEvent(event, fx = null, { leaves = [] } = {}) {
  const name = event.label ?? EVENT_KIND_LABELS[event.kind];
  if (event.kind === 'leave') {
    const days = daysBetween(event.date, event.endDate) + 1;
    const n = event.vestingDelayPeriods ?? 0;
    return {
      title: name,
      details: [
        {
          key: 'dates',
          label: 'Dates',
          value: `${shortDate(event.date)} – ${shortDate(event.endDate)}`,
        },
        { key: 'duration', label: 'Duration', value: count(days, 'day', 'days') },
        {
          key: 'vesting-delay',
          label: 'Vesting delay',
          value: n > 0 ? count(n, 'period', 'periods') : null,
        },
      ],
    };
  }
  if (event.kind !== 'review') {
    const n = event.payPeriods ?? 1;
    const v = event.value;
    const each = { ...v, amount: Math.floor(v.amount / n) };
    return {
      title: `${name}: ${formatMoneyValue(v, { whole: true })}`,
      details: [
        { key: 'date', label: 'Paid on', value: shortDate(event.date) },
        {
          key: 'spread',
          label: 'Paid over',
          value: n > 1 ? `${n} pays (${formatMoneyValue(each)} each)` : null,
        },
        { key: 'super', label: 'Super', value: event.superApplies ? 'Applies' : null },
      ],
    };
  }
  const details = [
    { key: 'date', label: 'From', value: shortDate(event.date) },
    {
      key: 'base',
      label: 'Salary',
      value: event.base ? formatMoneyValue(event.base, { whole: true }) : null,
    },
    {
      key: 'super',
      label: 'Super',
      value: event.super ? superValue(event.super.mode === 'sg' ? null : event.super.rate) : null,
    },
    { key: 'sacrifice', label: 'Sacrifice', value: sacrificeValue(event.sacrifice) },
    { key: 'bonus', label: 'Bonus', value: bonusTargetValue(event.bonusTarget) },
  ];
  const g = event.grant;
  if (g) {
    const tranches = grantTranches(g, { leaves, grantedOn: event.date });
    const known = tranches.filter((t) => t.actual !== null).length;
    const rate = g.total.currency === 'AUD' ? null : audRateFor(g.total.currency, { usdRate: fx });
    const audTotal = rate ? toAudCents(g.total, rate) : null;
    // A no-break space keeps "≈" with its amount.
    const aud = audTotal !== null ? ` (≈\u00a0${wholeMoney(audTotal)})` : '';
    const delayed = tranches.filter((t) => t.delayedPeriods > 0).length;
    const terms = [
      ...(known ? [`${known} of ${g.tranches} tranche amounts entered`] : []),
      ...(g.keepAfterEnd ? ['Kept after leaving'] : []),
      ...(!g.superApplies ? ['No super'] : []),
    ];
    details.push(
      {
        key: 'grant',
        label: 'Cash grant',
        value: `${formatMoneyValue(g.total, { whole: true })}${aud}`,
      },
      {
        key: 'grant-vesting',
        label: 'Cash vesting',
        value: vestingValue(
          g.tranches,
          g.intervalMonths,
          g.firstVest,
          delayed &&
            `${count(delayed, 'tranche', 'tranches')} delayed by unpaid leave (last vest ${shortDate(tranches.at(-1).date)})`,
        ),
      },
      { key: 'grant-terms', label: 'Cash terms', value: terms.join(' · ') || null },
    );
  }
  const s = event.shareGrant;
  if (s) {
    const dates = vestDates(s, { leaves, grantedOn: event.date });
    const price = s.grantPrice ? ` at ${formatMoneyValue(s.grantPrice, { price: true })}` : '';
    const delayed = dates.filter((t) => t.delayedPeriods > 0).length;
    const terms = [
      ...(s.growthRate ? [`Assumed growth ${formatPercent(s.growthRate)} a year`] : []),
      ...(s.sellToCover ? [`${formatPercent(s.sellToCover)} sold for tax`] : []),
      ...(s.keepAfterEnd ? ['Kept after leaving'] : []),
    ];
    details.push(
      {
        key: 'share-grant',
        label: 'Share grant',
        value: `${s.totalShares.toLocaleString('en-AU')} ${s.ticker}${price}`,
      },
      {
        key: 'share-vesting',
        label: 'Share vesting',
        value: vestingValue(
          s.tranches,
          s.intervalMonths,
          s.firstVest,
          delayed &&
            `${count(delayed, 'vest', 'vests')} delayed by unpaid leave (last vest ${shortDate(dates.at(-1).date)})`,
        ),
      },
      { key: 'share-terms', label: 'Share terms', value: terms.join(' · ') || null },
    );
  }
  if (details.every((d) => d.key === 'date' || d.value === null)) {
    details.push({ key: 'changes', label: 'Changes', value: 'None' });
  }
  return { title: name, details };
}

/**
 * FYs for the selector, newest first: next FY back to the earlier of the first
 * employment's FY and `pastYears` before now (the concessional carry-forward
 * window — prior-year figures feed this year's caps).
 */
export function fyOptions(employments, today = new Date(), { pastYears = 5 } = {}) {
  const now = currentFy(today);
  const first = Math.min(now - pastYears, ...employments.map((e) => fyOf(e.startDate)));
  const out = [];
  for (let fy = now + 1; fy >= first; fy--) out.push({ value: fy, label: fyLabel(fy) });
  return out;
}

/**
 * The "Share scheme income" tile hint (income + shares pages): vested shares
 * count as taxable income; any tax already covered by selling some of them.
 */
export function shareIncomeHint(withheldCents) {
  return withheldCents
    ? `Vested shares count as income. ${wholeMoney(withheldCents)} of tax was paid by selling some`
    : 'Vested shares count as income, taxed when you lodge your return';
}

/** Headline figures for the FY summary tiles: [{ label, cents, hint? }]. */
export function summaryTiles(summary) {
  const cc = summary.caps.concessional;
  const room = cc.capCents + cc.carryForwardAvailableCents - cc.contributedCents;
  return [
    { key: 'gross', label: 'Gross pay', cents: summary.grossCents, hint: 'Before tax' },
    {
      key: 'withheld',
      label: 'Tax withheld',
      cents: summary.withheldCents,
      hint: 'Taken from your pay and sent to the tax office',
    },
    { key: 'net', label: 'Net pay', cents: summary.netCents, hint: 'What you’re paid after tax' },
    {
      key: 'super',
      label: 'Employer super',
      cents: summary.employerSuperCents,
      hint: 'Paid into your super on top of your pay, not including salary sacrifice',
    },
    {
      key: 'cc-room',
      label: room >= 0 ? 'Super cap left' : 'Over the super cap',
      cents: Math.abs(room),
      tone: room >= 0 ? null : 'bad',
      hint: `Before-tax (concessional) super: ${wholeMoney(cc.contributedCents)} of ${wholeMoney(
        cc.capCents,
      )} used${
        cc.carryForwardAvailableCents
          ? `, plus ${wholeMoney(cc.carryForwardAvailableCents)} carried over from earlier years`
          : ''
      }`,
    },
    ...(summary.shareIncomeCents > 0
      ? [
          {
            key: 'share-income',
            label: 'Share scheme income',
            cents: summary.shareIncomeCents,
            hint: shareIncomeHint(summary.shareWithheldCents),
          },
        ]
      : []),
  ];
}

/** One pay's payslip lines: [{ label, cents, sign?, strong?, muted? }]. */
export function payslipRows(pay) {
  const rows = [{ label: 'Base pay', cents: pay.baseGrossCents }];
  if (pay.sacrificeCents)
    rows.push({ label: 'Salary sacrifice', cents: pay.sacrificeCents, sign: -1 });
  for (const x of pay.extras) {
    rows.push({ label: x.estimated ? `${x.label} (estimate)` : x.label, cents: x.amountCents });
  }
  rows.push({ label: 'Gross', cents: pay.grossCents, strong: true });
  rows.push({
    label: 'Tax withheld',
    cents: pay.taxCents + pay.additionalWithholdingCents,
    sign: -1,
  });
  if (pay.stslCents) rows.push({ label: 'Study loan', cents: pay.stslCents, sign: -1 });
  rows.push({ label: 'Net pay', cents: pay.netCents, strong: true });
  rows.push({
    label: `Employer super (${formatPercent(pay.superRate)}${pay.mcbLimited ? ', capped at the maximum contribution base' : ''})`,
    cents: pay.employerSuperCents,
    muted: true,
  });
  return rows;
}

/**
 * Signed display of a row amount ("-AU$100.00" for deductions); `opts` pass
 * to `formatMoney` (e.g. `short` in a table whose caption names the currency).
 */
export function rowAmount(row, opts = {}) {
  return formatMoney(row.sign === -1 ? -row.cents : row.cents, opts);
}

/** A rate for a % input: 0.12 → "12" ('' when null). */
export function percentInputValue(rate) {
  return rate === null || rate === undefined ? '' : String(Math.round(rate * 1e6) / 1e4);
}

/** A % input's text → a rate (12 → 0.12); null when blank; NaN when invalid. */
export function parsePercent(text) {
  const s = String(text ?? '')
    .trim()
    .replace(/%$/, '');
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 ? n / 100 : NaN;
}
